import { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { Users, UserPlus, AlertTriangle, Crown, Search } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { CandidateListItem } from './CandidateListItem'
import { CandidateDetailModal } from './CandidateDetailModal'
import { RoleAssignmentModal } from './RoleAssignmentModal'
import { CandidateRoleBadge } from './CandidateRoleBadge'
import { CandidateStatBar } from './CandidateStatBar'
import { IdeologyAlignmentBadge } from '@/components/ideology/IdeologyAlignmentBadge'
import { useCandidatesByParty } from '@/hooks/useCandidatesByParty'
import type { Candidate, IdeologyVector, CandidateRole } from '@/types'
import { cn } from '@/lib/utils'

interface CandidateListProps {
  partyId: string
  nationId: string
  partyIdeology: IdeologyVector
  isOwner?: boolean
  className?: string
}

type SortOption = 'name' | 'loyalty' | 'recognition' | 'role'
type FilterOption = 'all' | 'assigned' | 'unassigned'

const SORT_OPTIONS: { value: SortOption; label: string }[] = [
  { value: 'loyalty', label: 'Loyalty' },
  { value: 'recognition', label: 'Recognition' },
  { value: 'name', label: 'Name' },
  { value: 'role', label: 'Role' },
]

const FILTER_OPTIONS: { value: FilterOption; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'assigned', label: 'Assigned' },
  { value: 'unassigned', label: 'Unassigned' },
]

const ROLE_PRIORITY: Record<CandidateRole, number> = {
  party_leader: 0,
  minister: 1,
  chair: 2,
  regional_candidate: 3,
  list_candidate: 4,
  none: 5,
}

function sortCandidates(
  candidates: Candidate[],
  sortBy: SortOption
): Candidate[] {
  return [...candidates].sort((a, b) => {
    switch (sortBy) {
      case 'name':
        return a.name.localeCompare(b.name)
      case 'loyalty':
        return b.loyalty - a.loyalty
      case 'recognition':
        return b.recognition - a.recognition
      case 'role':
        return ROLE_PRIORITY[a.role] - ROLE_PRIORITY[b.role]
      default:
        return 0
    }
  })
}

function filterCandidates(
  candidates: Candidate[],
  filter: FilterOption,
  searchQuery: string
): Candidate[] {
  let filtered = candidates

  // Apply role filter
  if (filter === 'assigned') {
    filtered = filtered.filter((c) => c.role !== 'none')
  } else if (filter === 'unassigned') {
    filtered = filtered.filter((c) => c.role === 'none')
  }

  // Apply search filter
  if (searchQuery.trim()) {
    const query = searchQuery.toLowerCase().trim()
    filtered = filtered.filter((c) => c.name.toLowerCase().includes(query))
  }

  return filtered
}

function PartyLeaderCard({
  leader,
  partyIdeology,
  onClick,
}: {
  leader: Candidate
  partyIdeology: IdeologyVector
  onClick?: () => void
}) {
  return (
    <Card
      className={cn(
        'border-amber-500/50 bg-amber-500/5',
        onClick && 'cursor-pointer hover:border-amber-500/80 transition-colors'
      )}
      onClick={onClick}
    >
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <Crown className="h-5 w-5 text-amber-500" />
          <CardTitle className="text-base">Your Party Leader</CardTitle>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-start justify-between gap-2">
          <div>
            <p className="font-semibold text-lg">{leader.name}</p>
            <p className="text-muted-foreground text-sm">Age {leader.age}</p>
          </div>
          <CandidateRoleBadge role="party_leader" />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5">
          <CandidateStatBar
            label="Loyalty"
            value={leader.loyalty}
            colorMode="positive"
          />
          <CandidateStatBar
            label="Recognition"
            value={leader.recognition}
            colorMode="positive"
          />
          <CandidateStatBar
            label="Charisma"
            value={leader.charisma}
            colorMode="positive"
          />
          <CandidateStatBar
            label="Debate"
            value={leader.debate_skill}
            colorMode="positive"
          />
        </div>

        <div className="pt-2 border-t flex items-center justify-between">
          <span className="text-muted-foreground text-sm">Ideology fit:</span>
          <IdeologyAlignmentBadge
            candidateIdeology={leader.ideology_vector}
            partyIdeology={partyIdeology}
          />
        </div>
      </CardContent>
    </Card>
  )
}

function NoLeaderWarning() {
  return (
    <div className="flex items-center gap-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-600 dark:text-amber-400">
      <AlertTriangle className="h-4 w-4 shrink-0" />
      <p className="text-sm">
        No party leader assigned. Some features require a leader.
      </p>
    </div>
  )
}

function EmptyState({ hasFilters }: { hasFilters: boolean }) {
  if (hasFilters) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-center space-y-2">
        <Search className="h-8 w-8 text-muted-foreground" />
        <p className="text-sm font-medium">No candidates match your filters</p>
        <p className="text-sm text-muted-foreground">
          Try adjusting your search or filter settings.
        </p>
      </div>
    )
  }

  return (
    <div className="flex flex-col items-center justify-center py-8 text-center space-y-3">
      <div className="rounded-full bg-muted p-3">
        <Users className="h-6 w-6 text-muted-foreground" />
      </div>
      <div className="space-y-1">
        <p className="text-sm font-medium">No candidates yet</p>
        <p className="text-sm text-muted-foreground">
          Recruit candidates to run for office!
        </p>
      </div>
    </div>
  )
}

function LoadingState() {
  return (
    <div className="space-y-3">
      {[1, 2, 3].map((i) => (
        <div key={i} className="h-32 bg-muted animate-pulse rounded-lg" />
      ))}
    </div>
  )
}

export function CandidateList({
  partyId,
  nationId,
  partyIdeology,
  isOwner = false,
  className,
}: CandidateListProps) {
  const { candidates, isLoading, error } = useCandidatesByParty(partyId)
  const [sortBy, setSortBy] = useState<SortOption>('loyalty')
  const [filterBy, setFilterBy] = useState<FilterOption>('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [showAll, setShowAll] = useState(false)
  const [selectedCandidateId, setSelectedCandidateId] = useState<string | null>(null)
  const [roleAssignmentCandidateId, setRoleAssignmentCandidateId] = useState<string | null>(null)

  const handleChangeRole = (candidateId: string) => {
    setRoleAssignmentCandidateId(candidateId)
  }

  const handleRoleAssignmentSuccess = () => {
    // Close the detail modal as well since the role may have changed
    setSelectedCandidateId(null)
  }

  const partyLeader = useMemo(
    () => candidates?.find((c) => c.role === 'party_leader') ?? null,
    [candidates]
  )

  const nonLeaderCandidates = useMemo(
    () => candidates?.filter((c) => c.role !== 'party_leader') ?? [],
    [candidates]
  )

  const filteredCandidates = useMemo(
    () => filterCandidates(nonLeaderCandidates, filterBy, searchQuery),
    [nonLeaderCandidates, filterBy, searchQuery]
  )

  const sortedCandidates = useMemo(
    () => sortCandidates(filteredCandidates, sortBy),
    [filteredCandidates, sortBy]
  )

  const displayedCandidates = showAll
    ? sortedCandidates
    : sortedCandidates.slice(0, 5)

  const hasFilters = filterBy !== 'all' || searchQuery.trim().length > 0
  const candidateCount = candidates?.length ?? 0

  if (error) {
    return (
      <Card className={className}>
        <CardContent className="py-6">
          <p className="text-destructive text-center">
            Failed to load candidates: {error.message}
          </p>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className={className}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="text-base">
            Candidates{' '}
            {!isLoading && (
              <span className="text-muted-foreground font-normal">
                ({candidateCount})
              </span>
            )}
          </CardTitle>
          <Button asChild variant="outline" size="sm">
            <Link to={`/party/${partyId}#recruitment`}>
              <UserPlus className="h-4 w-4 mr-1.5" />
              Recruit More
            </Link>
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? (
          <LoadingState />
        ) : (
          <>
            {/* No leader warning */}
            {!partyLeader && candidateCount > 0 && <NoLeaderWarning />}

            {/* Party Leader section */}
            {partyLeader && (
              <PartyLeaderCard
                leader={partyLeader}
                partyIdeology={partyIdeology}
                onClick={() => setSelectedCandidateId(partyLeader.id)}
              />
            )}

            {/* Filters */}
            {candidateCount > 0 && (
              <div className="flex flex-col sm:flex-row gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by name..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-8 h-9"
                  />
                </div>
                <div className="flex gap-2">
                  <Select
                    value={filterBy}
                    onValueChange={(v) => setFilterBy(v as FilterOption)}
                  >
                    <SelectTrigger className="w-[120px] h-9">
                      <SelectValue placeholder="Filter" />
                    </SelectTrigger>
                    <SelectContent>
                      {FILTER_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select
                    value={sortBy}
                    onValueChange={(v) => setSortBy(v as SortOption)}
                  >
                    <SelectTrigger className="w-[130px] h-9">
                      <SelectValue placeholder="Sort" />
                    </SelectTrigger>
                    <SelectContent>
                      {SORT_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {/* Candidate list */}
            {candidateCount === 0 ? (
              <EmptyState hasFilters={false} />
            ) : sortedCandidates.length === 0 ? (
              <EmptyState hasFilters={hasFilters} />
            ) : (
              <div className="space-y-3">
                {displayedCandidates.map((candidate) => (
                  <CandidateListItem
                    key={candidate.id}
                    candidate={candidate}
                    partyIdeology={partyIdeology}
                    onClick={() => setSelectedCandidateId(candidate.id)}
                  />
                ))}
              </div>
            )}

            {/* Show all / Show less */}
            {sortedCandidates.length > 5 && (
              <div className="flex items-center justify-between pt-2 border-t">
                <p className="text-sm text-muted-foreground">
                  Showing {displayedCandidates.length} of{' '}
                  {sortedCandidates.length} candidates
                </p>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowAll(!showAll)}
                >
                  {showAll ? 'Show Less' : 'Show All'}
                </Button>
              </div>
            )}
          </>
        )}
      </CardContent>

      {/* Candidate Detail Modal */}
      <CandidateDetailModal
        candidateId={selectedCandidateId}
        isOpen={selectedCandidateId !== null}
        onClose={() => setSelectedCandidateId(null)}
        partyIdeology={partyIdeology}
        isOwner={isOwner}
        onChangeRole={isOwner ? handleChangeRole : undefined}
      />

      {/* Role Assignment Modal */}
      <RoleAssignmentModal
        candidateId={roleAssignmentCandidateId}
        partyId={partyId}
        nationId={nationId}
        isOpen={roleAssignmentCandidateId !== null}
        onClose={() => setRoleAssignmentCandidateId(null)}
        onSuccess={handleRoleAssignmentSuccess}
      />
    </Card>
  )
}
